<script setup lang="ts">
/**
 * Interim Dividends (B32113) — SG 일반 프리셋 Studio 패널.
 *   문서 2종: Directors' Resolutions (Interim Dividend) — 회사 1부, 이사 전원 서명([[Signers]])
 *             Dividend Voucher — 주주 1인당 1부(수동 add/remove), 이사 1인 서명([[Signing_Director_Signature]])
 *   회사 선택 → 회사·등록사무소·FYE·주주(이름·주식수·주소)·이사(서명자) 자동채움 → 바로 문서 화면.
 *   주주구성(Sole 개인/법인 · Multiple) 판별 없음 — voucher 부수만 주주 수를 따른다.
 *   배당액: 주주 1명 = 총배당액을 그 주주 금액으로 미러 / 다수 = 주주별 금액 합계를 총배당액으로.
 *   입력 엔진(cdd)·서명 컨텍스트·정규화는 Final Dividends(B32112) 와 공유(토큰 이름이 같다).
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, typeOf } from './final-dividends/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from './final-dividends/cdd/agmSignersContext';
import { normalizeFinalDividendsForWord } from './final-dividends/utils/normalizeFinalDividendsForWord';
import InterimDividendsPackageDoc from './interim-dividends/InterimDividendsPackageDoc.vue';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const docRef = ref<InstanceType<typeof InterimDividendsPackageDoc> | null>(null);
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const autoLocal = ref<{ memberIds: number[] }>({ memberIds: [1] });
const taskConfigSteps = ref<string[]>([]);

const TASK_CODE = 'B32113';

/**
 * Voucher(주주) 스코프에만 속하는 키. 지급일(payment_date)·총배당액·FYE 는 회사 공통이라 여기 없다
 *   — voucher 화면도 그 칸을 최상위 키로 묶어 DR 과 같은 값을 쓴다.
 */
const MEMBER_OWNED = ['member_name', 'member_address', 'number_of_shares', 'amount_to_receive', 'dividend_number'];

function isoDate(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : s;
}
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `id-sg-${signerSeq++}`, name: String(name || '').trim(), title, role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const pct = Math.min(100, Math.round((fill.filledCount.value / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    const isMember = bareKey(key) === 'member_name';
    emit('request-person-lookup', {
      fieldGroup: 'sg-interim-dividends', key: bareKey(key), scopedKey: key,
      defaultRole: isMember ? 'SHAREHOLDER' : 'CONTACT', lookupType: 'officer', entityCd: isMember ? entityCd.value : '',
    });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

/** getContactDetail 응답에서 주소 한 줄 추출(개인=거주지 / 법인=등록사무소). */
function contactAddress(c: any): string {
  const direct = String(
    c?.residentialAddress || c?.primaryAddress || c?.registeredAddress
    || c?.registeredOfficeAddress || c?.contactAddress || c?.address || '',
  ).trim();
  if (direct) return direct;
  const arr = Array.isArray(c?.addresses) ? c.addresses : [];
  if (arr.length) {
    const a = arr[0];
    if (typeof a === 'string') return a.trim();
    return [
      a.line1 || a.addressLine1 || a.block, a.line2 || a.addressLine2 || a.street,
      [a.postalCode || a.postcode, a.city].filter(Boolean).join(' '), a.country,
    ].map((s: any) => String(s || '').trim()).filter(Boolean).join(', ');
  }
  return '';
}

// ── 자동채움: 회사 선택 → 회사·등록사무소·FYE·주주·이사(서명자) ──
async function autofillFromEntity(entityCdRaw: string) {
  const cd = String(entityCdRaw || '').trim();
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) {
    signersCtx.clearAll();
    for (const k of Object.keys(fill.values)) { if (/^mem_\d+\//.test(k)) fill.setValue(k, ''); }
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => { const s = String(v ?? '').trim(); if (s) { fill.setValue(k, s); written.add(k); } };

  let members: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
    set('financial_year_end', isoDate(src.financialYearEnd));
    members = Array.isArray(src.members) ? src.members.filter((m: any) => m?.name) : [];
  } catch (e) { console.warn('[InterimDividends] agm-source failed', e); }

  // 이사(SG) — officers-by-entity-all 중 이사(positionCd B30027 / positionName 'director').
  const directorNames: string[] = [];
  try {
    const res: any = await docGenApi.getEntityOfficersAll(cd);
    const officers: any[] = res?.data?.officers ?? res?.data?.data?.officers ?? [];
    for (const o of officers) {
      const positions: any[] = Array.isArray(o?.positions) ? o.positions : [];
      const isDir = positions.some((p) => String(p?.positionCd || '') === 'B30027' || /director/i.test(String(p?.positionName || '')));
      if (!isDir) continue;
      const nm = String(o?.contactName || o?.name || o?.fullName || '').trim();
      if (nm && !directorNames.includes(nm)) directorNames.push(nm);
    }
  } catch (e) { console.warn('[InterimDividends] officers-by-entity-all failed', e); }
  if (directorNames.length) {
    if (!signersCtx.signersOf('dr_Signers').length) signersCtx.setSigners('dr_Signers', directorNames.map((nm) => mkSigner(nm, 'Director')));
    if (!signersCtx.signersOf('signing_director_Signers').length) signersCtx.setSigners('signing_director_Signers', [mkSigner(directorNames[0], 'Director')]);
  }

  // 주주 → Voucher 부수 (주주 1인당 1부)
  const memberIds: number[] = [];
  members.forEach((m, i) => {
    const id = i + 1;
    set(`mem_${id}/member_name`, m.name);
    set(`mem_${id}/dividend_number`, String(id)); // Final Dividends 와 같은 규칙(voucher 순번), 수정 가능
    const shares = Number(m.sharesTotal || m.sharesOrdinary || 0);
    if (shares > 0) set(`mem_${id}/number_of_shares`, shares.toLocaleString());
    memberIds.push(id);
  });
  if (!memberIds.length) memberIds.push(1);

  // 각 Voucher 의 자동 서명자 = 첫 이사 (스코프 키라 전역 시드로는 안 들어감)
  if (directorNames[0]) {
    for (const id of memberIds) {
      const sk = `mem_${id}/signing_director_Signers`;
      if (!signersCtx.signersOf(sk).length) signersCtx.setSigners(sk, [mkSigner(directorNames[0], 'Director')]);
    }
  }

  // 주주 주소 — agm-source 멤버엔 주소가 없어 contactCd 로 상세 조회(개인=거주지/법인=등록사무소).
  await Promise.all(members.map(async (m, i) => {
    const id = i + 1;
    let addr = String(m?.registeredOfficeAddress || m?.address || m?.residentialAddress || '').trim();
    const contactCd = String(m?.contactCd || '').trim();
    if (!addr && contactCd) {
      try {
        const cRes: any = await docGenApi.getContactDetail(cd, contactCd, { addressScope: 'person' });
        addr = contactAddress(cRes?.data?.data || cRes?.data || cRes || {});
      } catch (e) { console.warn('[InterimDividends] member address enrich failed', e); }
    }
    if (addr) set(`mem_${id}/member_address`, addr);
  }));

  autoLocal.value = { memberIds };
  autofilledKeys.value = written;
  emitProgress();
}

watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  fill.applyPersonToActive(p as Record<string, any>);
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

// ── 배당액 자동 계산 ─────────────────────────────────────────────
//   주주 다수: 주주별 금액 합계 → total_dividend_amount + dividend_amount.
//   주주 1명 : DR 총배당액 ↔ voucher 금액 양방향(어느 쪽을 먼저 입력해도 나머지가 채워짐).
let recomputing = false;
function parseAmt(s: unknown): number {
  const n = parseFloat(String(s ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
function amtPrefix(s: unknown): string {
  const m = String(s ?? '').match(/^[^0-9.]+/);
  return m ? m[0] : '';
}
function fmtAmt(n: number, prefix: string): string {
  const body = n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 });
  return prefix ? `${prefix}${body}` : body;
}
function currentMemberIds(): number[] {
  const l: any = docRef.value?.gatherLocalState?.();
  const ids = Array.isArray(l?.memberIds) && l.memberIds.length ? l.memberIds : autoLocal.value.memberIds;
  return (ids && ids.length) ? ids : [1];
}
/**
 * @param source 무엇이 바뀌어서 다시 계산하는지.
 *   'members' = voucher 의 주주별 금액, 'total' = 결의서(DR)의 총 배당액.
 *
 *   주주 다수: 주주별 금액 합계 → 총배당액(DR) + Total Dividend Amount(voucher).
 *   주주 1명 : 두 값은 항상 같아야 하므로 **어느 쪽을 입력하든 양방향으로 맞춘다**.
 *     예전엔 DR → voucher 한 방향만 있어서, voucher 금액부터 입력하면 DR·Total 이 빈칸으로 남았다.
 */
function recomputeDividendAmounts(source: 'members' | 'total' = 'members') {
  if (recomputing) return;
  const ids = currentMemberIds();
  recomputing = true;
  try {
    let prefix = ''; let sum = 0; let any = false;
    for (const id of ids) {
      const v = String(fill.values[`mem_${id}/amount_to_receive`] ?? '').trim();
      if (v) { any = true; if (!prefix) prefix = amtPrefix(v); sum += parseAmt(v); }
    }
    if (ids.length > 1) {
      if (any) {
        const t = fmtAmt(sum, prefix);
        fill.setValue('total_dividend_amount', t);
        fill.setValue('dividend_amount', t);
      }
    } else {
      const memberKey = `mem_${ids[0] ?? 1}/amount_to_receive`;
      const memberStr = String(fill.values[memberKey] ?? '').trim();
      const totalStr = String(fill.values.dividend_amount ?? '').trim();
      if (source === 'total' && parseAmt(totalStr)) {
        // DR 총배당액을 바꿈 → voucher 금액·Total 도 같은 값
        fill.setValue('total_dividend_amount', totalStr);
        fill.setValue(memberKey, totalStr);
      } else if (parseAmt(memberStr)) {
        // voucher 금액을 바꿈 → DR 총배당액·Total 도 같은 값
        fill.setValue('total_dividend_amount', memberStr);
        fill.setValue('dividend_amount', memberStr);
      } else if (parseAmt(totalStr)) {
        fill.setValue('total_dividend_amount', totalStr);
        fill.setValue(memberKey, totalStr);
      }
    }
  } finally { recomputing = false; }
  emitProgress();
}
watch(() => currentMemberIds().map((id) => fill.values[`mem_${id}/amount_to_receive`] ?? '').join('|'), () => recomputeDividendAmounts('members'));
watch(() => fill.values.dividend_amount, () => recomputeDividendAmounts('total'));
watch(() => autoLocal.value.memberIds, () => recomputeDividendAmounts('members'), { deep: true });

/**
 * 생성 문서용 금액 표기 — 숫자만 입력했으면 "SGD 6,666" 처럼 통화·천 단위 쉼표를 붙인다.
 *   (화면 입력값은 그대로 두고 문서에만 적용. "USD 100" 처럼 통화를 직접 쓴 값은 건드리지 않음)
 */
const AMOUNT_KEYS = ['dividend_amount', 'total_dividend_amount', 'amount_to_receive'];
function withCurrency(v: unknown): unknown {
  const s = String(v ?? '').trim();
  if (!s || !/^[\d,.\s]+$/.test(s)) return v;
  return `SGD ${fmtAmt(parseAmt(s), '')}`;
}
function formatAmounts(fd: Record<string, any>): Record<string, any> {
  for (const k of AMOUNT_KEYS) if (k in fd) fd[k] = withCurrency(fd[k]);
  return fd;
}

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}
function restoreState(s: any) {
  if (!s) return;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) docRef.value?.restoreLocalState?.(s.local);
}

/** 템플릿 → 문서 종류. 결의서 1부 / Voucher 주주 수만큼. */
function classifyDoc(name: string): 'dr' | 'voucher' | 'other' {
  const n = String(name || '').toLowerCase().replace(/[_\-.]+/g, ' ');
  if (/dividend voucher|voucher/.test(n)) return 'voucher';
  if (/resolution|directors/.test(n)) return 'dr';
  return 'other';
}

function companyFormData(): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(fill.exportPayload())) { if (!k.includes('/')) out[k] = v; }
  return out;
}
function scopedFormData(scopePrefix: string): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) {
    if (k.includes('/')) continue;
    if (MEMBER_OWNED.includes(bareKey(k))) continue;
    out[k] = v;
  }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scopePrefix)) continue;
    const bk = bareKey(k);
    const sv = String(v ?? '').trim();
    if (sv === '' && bk in out && String(out[bk] ?? '').trim() !== '') continue;
    out[bk] = v;
  }
  return out;
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    const memberIds = currentMemberIds();

    // 이사 서명(전원 = [[Signers]] / 단일 = [[Signing_Director_Signature]])
    const boardSigners = signersCtx.signersOf('dr_Signers');
    const singleSigGlobal = signersCtx.signersOf('signing_director_Signers').length
      ? signersCtx.signersOf('signing_director_Signers')
      : (boardSigners.length ? [boardSigners[0]] : []);

    const companyBaseRaw = formatAmounts(companyFormData());
    const companyBase = normalizeFinalDividendsForWord(companyBaseRaw);
    const companyLabel = String(companyBase.Company_Name || companyBase.company_name || 'Company');

    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SG_INTERIM_DIVIDENDS',
      formData: companyBase,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Interim Dividends',
          taskTitle: effectiveTitle.value,
          taskFlow: { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') },
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }

    progress.value = 'Preparing documents…';
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid); return true;
    });
    type DocSave = { templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: any[] };
    const docsToSave: DocSave[] = [];
    function pushOnce(d: any, formData: Record<string, any>, person: string, suffix = '', signers?: any[]) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '').split(' — ')[0].trim();
      docsToSave.push({
        templateId: tid,
        label: suffix ? `${baseLabel} — ${suffix}` : baseLabel,
        personName: person,
        formData: normalizeFinalDividendsForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    for (const d of docs) {
      const kind = classifyDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'voucher') {
        for (const id of memberIds) {
          const fd = formatAmounts(scopedFormData(`mem_${id}/`));
          // 템플릿이 서명란 아래에 "Director" 를 고정 문구로 갖고 있어, 서명 박스의 직함은 숨긴다
          //   (안 숨기면 "Director" 가 두 줄로 찍힌다).
          const vSig = (signersCtx.signersOf(`mem_${id}/signing_director_Signers`).length
            ? signersCtx.signersOf(`mem_${id}/signing_director_Signers`) : singleSigGlobal)
            .map((s) => ({ ...s, showTitle: false }));
          fd.Signing_Director_Signature = vSig; // 렌더용([[Signing_Director_Signature]])
          const person = String(fd.member_name || `Member ${id}`);
          // 다건이면 문서명에 주주 이름(예: "Dividend Voucher — ALICE TAN"). 서명자는 SIGNER_JSON 으로도 저장.
          pushOnce(d, fd, person, memberIds.length > 1 ? person : '', vSig);
        }
        continue;
      }
      if (kind === 'dr') {
        pushOnce(d, { ...companyBaseRaw }, companyLabel, '', boardSigners);
        continue;
      }
      pushOnce(d, { ...companyBaseRaw }, companyLabel);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) { progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`; }
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="fd-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Interim Dividends' }}</div>
        <div class="co-subtitle">Interim Dividends — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <InterimDividendsPackageDoc ref="docRef" :auto-local="autoLocal" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.fd-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.fd-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
